import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { NotFoundPage, RouteErrorPage } from '@/features/misc/ErrorPage';
import { CircularProgress, Box } from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/InsightsOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import BrushIcon from '@mui/icons-material/BrushOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import DashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenuOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurantOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import ViewCarouselIcon from '@mui/icons-material/ViewCarouselOutlined';
import ViewQuiltIcon from '@mui/icons-material/ViewQuiltOutlined';
import GridViewIcon from '@mui/icons-material/GridViewOutlined';

import { AppShell, type NavItem } from '@/components/AppShell';
import { CommandPaletteProvider } from '@/components/CommandPalette';
import {
  AuthGate,
  homePathFor,
  RedirectIfAuthenticated,
  RequireAuth,
} from '@/features/auth/RequireAuth';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  DASHBOARD_ORIGIN,
  isDashboardHost,
  isMarketingHost,
} from '@/features/auth/siteHosts';

const LoginPage = lazy(() =>
  import('@/features/auth/AuthPages').then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/features/auth/AuthPages').then((module) => ({ default: module.RegisterPage })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/OnboardingPage').then((module) => ({
    default: module.OnboardingPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);
const MenusPage = lazy(() =>
  import('@/features/menus/MenusPage').then((module) => ({ default: module.MenusPage })),
);
const MenuEditorPage = lazy(() =>
  import('@/features/menus/MenuEditorPage').then((module) => ({
    default: module.MenuEditorPage,
  })),
);
const BrandStudioPage = lazy(() =>
  import('@/features/brand/BrandStudioPage').then((module) => ({
    default: module.BrandStudioPage,
  })),
);
const InstancesPage = lazy(() =>
  import('@/features/instances/InstancesPage').then((module) => ({
    default: module.InstancesPage,
  })),
);
const InstanceDetailPage = lazy(() =>
  import('@/features/instances/InstanceDetailPage').then((module) => ({
    default: module.InstanceDetailPage,
  })),
);
const InstancePrintPage = lazy(() =>
  import('@/features/instances/InstancePrintPage').then((module) => ({
    default: module.InstancePrintPage,
  })),
);
const InstanceDesignPage = lazy(() =>
  import('@/features/instances/InstanceDesignPage').then((module) => ({
    default: module.InstanceDesignPage,
  })),
);
const BillingPage = lazy(() =>
  import('@/features/billing/BillingPage').then((module) => ({ default: module.BillingPage })),
);
const RestaurantAnalyticsPage = lazy(() =>
  import('@/features/analytics/AnalyticsPage').then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);
const AdminOverviewPage = lazy(() =>
  import('@/features/admin/AdminOverviewPage').then((module) => ({
    default: module.AdminOverviewPage,
  })),
);
const AdminTemplatesPage = lazy(() =>
  import('@/features/admin/AdminTemplatesPage').then((module) => ({
    default: module.AdminTemplatesPage,
  })),
);
const AdminTemplateVersionsPage = lazy(() =>
  import('@/features/admin/AdminTemplatesPage').then((module) => ({
    default: module.AdminTemplateVersionsPage,
  })),
);
const AdminRestaurantsPage = lazy(() =>
  import('@/features/admin/AdminRestaurantsPage').then((module) => ({
    default: module.AdminRestaurantsPage,
  })),
);
const AdminEnvPage = lazy(() =>
  import('@/features/admin/AdminEnvPage').then((module) => ({
    default: module.AdminEnvPage,
  })),
);
const AdminRestaurantDetailPage = lazy(() =>
  import('@/features/admin/AdminRestaurantDetailPage').then((module) => ({
    default: module.AdminRestaurantDetailPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/features/admin/AdminUsersPage').then((module) => ({
    default: module.AdminUsersPage,
  })),
);
const ConstructorPage = lazy(() =>
  import('@/features/constructor/ConstructorPage').then((module) => ({
    default: module.ConstructorPage,
  })),
);
const MarketplacePage = lazy(() =>
  import('@/features/marketplace/MarketplacePage').then((module) => ({
    default: module.MarketplacePage,
  })),
);
const LandingPage = lazy(() =>
  import('@/features/landing/LandingPage').then((module) => ({
    default: module.LandingPage,
  })),
);
const PublicMenuPage = lazy(() =>
  import('@/features/viewer/PublicMenuPage').then((module) => ({
    default: module.PublicMenuPage,
  })),
);
const DiningGuestPage = lazy(() =>
  import('@/features/dining/DiningGuestPage').then((module) => ({
    default: module.DiningGuestPage,
  })),
);
const TablesPage = lazy(() =>
  import('@/features/dining/TablesPage').then((module) => ({
    default: module.TablesPage,
  })),
);
const FloorPage = lazy(() =>
  import('@/features/dining/FloorPage').then((module) => ({
    default: module.FloorPage,
  })),
);
const RESTAURANT_NAV: NavItem[] = [
  { label: 'Overview', to: '/app', icon: <DashboardIcon fontSize="small" />, end: true },
  { label: 'Floor', to: '/app/floor', icon: <GridViewIcon fontSize="small" /> },
  { label: 'Tables', to: '/app/tables', icon: <TableRestaurantIcon fontSize="small" /> },
  { label: 'Templates', to: '/app/templates', icon: <ViewCarouselIcon fontSize="small" /> },
  { label: 'Menus', to: '/app/menus', icon: <RestaurantMenuIcon fontSize="small" /> },
  { label: 'Menu outputs', to: '/app/instances', icon: <AutoStoriesIcon fontSize="small" /> },
  { label: 'Brand Studio', to: '/app/brand', icon: <BrushIcon fontSize="small" /> },
  { label: 'Analytics', to: '/app/analytics', icon: <AnalyticsIcon fontSize="small" /> },
  { label: 'Plan', to: '/app/billing', icon: <CreditCardIcon fontSize="small" />, ownerOnly: true },
  { label: 'Settings', to: '/app/settings', icon: <SettingsIcon fontSize="small" /> },
];

const WAITER_NAV: NavItem[] = [
  { label: 'Floor', to: '/app/floor', icon: <GridViewIcon fontSize="small" /> },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Overview', to: '/admin/overview', icon: <DashboardIcon fontSize="small" /> },
  { label: 'Templates', to: '/admin', icon: <ViewQuiltIcon fontSize="small" />, end: true },
  { label: 'Restaurants', to: '/admin/restaurants', icon: <StorefrontIcon fontSize="small" /> },
  { label: 'Users', to: '/admin/users', icon: <BadgeIcon fontSize="small" /> },
  { label: 'Environment', to: '/admin/environment', icon: <TuneIcon fontSize="small" /> },
];

function Fallback() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60dvh' }}>
      <CircularProgress size={26} />
    </Box>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Fallback />}>{children}</Suspense>;
}

/**
 * Sends a restaurant that has never finished setup to the wizard.
 *
 * Placed here rather than in each page so a half-configured account cannot
 * reach a screen that assumes a menu, a brand and a template all exist.
 */
function RestaurantLayout() {
  const { restaurant, user } = useAuth();

  if (restaurant && !restaurant.onboardingComplete && user?.role !== 'WAITER') {
    return <Navigate to="/onboarding" replace />;
  }

  const nav = user?.role === 'WAITER' ? WAITER_NAV : RESTAURANT_NAV;

  return (
    <CommandPaletteProvider>
      <AppShell nav={nav} context={restaurant?.name}>
        <Lazy>
          <Outlet />
        </Lazy>
      </AppShell>
    </CommandPaletteProvider>
  );
}

function CrossOriginRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <Fallback />;
}

function HomeOrLanding() {
  const { user, loading } = useAuth();
  if (loading) return <Fallback />;

  if (isDashboardHost()) {
    if (user) return <Navigate to={homePathFor(user.role)} replace />;
    return <Navigate to="/login" replace />;
  }

  if (user && isMarketingHost()) {
    return <CrossOriginRedirect to={`${DASHBOARD_ORIGIN}${homePathFor(user.role)}`} />;
  }

  if (user) return <Navigate to={homePathFor(user.role)} replace />;

  return (
    <Lazy>
      <LandingPage />
    </Lazy>
  );
}

function AdminLayout() {
  return (
    <CommandPaletteProvider>
      <AppShell nav={ADMIN_NAV} context="Admin">
        <Lazy>
          <Outlet />
        </Lazy>
      </AppShell>
    </CommandPaletteProvider>
  );
}

const DEV_ERROR_PREVIEWS = import.meta.env.DEV
  ? [
      {
        path: '/__dev/crash',
        loader() {
          throw new Error('Intentional crash — previewing the Menuvera error page.');
        },
      },
    ]
  : [];

export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorPage />,
    children: [
      ...DEV_ERROR_PREVIEWS,
      {
        path: '/m/:slug',
        element: (
          <Lazy>
            <PublicMenuPage />
          </Lazy>
        ),
      },
      {
        // Alias for tablet / print-shop links that use /book/:slug.
        path: '/book/:slug',
        element: (
          <Lazy>
            <PublicMenuPage />
          </Lazy>
        ),
      },
      {
        path: '/t/:token',
        element: (
          <Lazy>
            <DiningGuestPage />
          </Lazy>
        ),
      },
      {
        path: '/',
        element: <HomeOrLanding />,
      },
      {
        // Legacy / marketing links; marketplace lives under the restaurant app.
        path: '/marketplace',
        element: <Navigate to="/app/templates" replace />,
      },
      {
        element: <AuthGate />,
        children: [
          {
            element: <RedirectIfAuthenticated />,
            children: [
              {
                path: '/login',
                element: (
                  <Lazy>
                    <LoginPage />
                  </Lazy>
                ),
              },
              {
                path: '/register',
                element: (
                  <Lazy>
                    <RegisterPage />
                  </Lazy>
                ),
              },
            ],
          },
          {
            element: (
              <RequireAuth roles={['RESTAURANT_OWNER', 'RESTAURANT_STAFF', 'WAITER']} />
            ),
            children: [
              {
                path: '/onboarding',
                element: (
                  <Lazy>
                    <OnboardingPage />
                  </Lazy>
                ),
              },
              {
                path: '/app/instances/:instanceId/print',
                element: (
                  <RequireAuth roles={['RESTAURANT_OWNER', 'RESTAURANT_STAFF']} />
                ),
                children: [
                  {
                    path: '',
                    element: (
                      <Lazy>
                        <InstancePrintPage />
                      </Lazy>
                    ),
                  },
                ],
              },
              {
                // Full-viewport design editor (same constructor UI as admin).
                path: '/app/instances/:instanceId/design',
                element: (
                  <RequireAuth roles={['RESTAURANT_OWNER', 'RESTAURANT_STAFF']} />
                ),
                children: [
                  {
                    path: '',
                    element: (
                      <CommandPaletteProvider>
                        <Lazy>
                          <InstanceDesignPage />
                        </Lazy>
                      </CommandPaletteProvider>
                    ),
                  },
                ],
              },
              {
                path: '/app',
                element: <RestaurantLayout />,
                children: [
                  {
                    element: (
                      <RequireAuth roles={['RESTAURANT_OWNER', 'RESTAURANT_STAFF']} />
                    ),
                    children: [
                      { index: true, element: <DashboardPage /> },
                      { path: 'tables', element: <TablesPage /> },
                      { path: 'templates', element: <MarketplacePage /> },
                      { path: 'menus', element: <MenusPage /> },
                      { path: 'menus/:menuId', element: <MenuEditorPage /> },
                      { path: 'instances', element: <InstancesPage /> },
                      { path: 'instances/:instanceId', element: <InstanceDetailPage /> },
                      { path: 'brand', element: <BrandStudioPage /> },
                      { path: 'analytics', element: <RestaurantAnalyticsPage /> },
                      { path: 'settings', element: <SettingsPage /> },
                    ],
                  },
                  {
                    element: (
                      <RequireAuth
                        roles={['RESTAURANT_OWNER', 'RESTAURANT_STAFF', 'WAITER']}
                      />
                    ),
                    children: [{ path: 'floor', element: <FloorPage /> }],
                  },
                  {
                    element: (
                      <RequireAuth
                        roles={['RESTAURANT_OWNER']}
                        deniedToast="Plan is for restaurant owners"
                      />
                    ),
                    children: [{ path: 'billing', element: <BillingPage /> }],
                  },
                ],
              },
            ],
          },
          {
            element: <RequireAuth roles={['ADMIN']} />,
            children: [
              {
                // The constructor owns the whole viewport, so it sits outside the
                // admin shell rather than fighting a sidebar for horizontal space.
                path: '/admin/constructor/:versionId',
                element: (
                  <CommandPaletteProvider>
                    <Lazy>
                      <ConstructorPage />
                    </Lazy>
                  </CommandPaletteProvider>
                ),
              },
              {
                path: '/admin',
                element: <AdminLayout />,
                children: [
                  { index: true, element: <AdminTemplatesPage /> },
                  { path: 'overview', element: <AdminOverviewPage /> },
                  { path: 'templates', element: <Navigate to="/admin" replace /> },
                  { path: 'templates/:templateId', element: <AdminTemplateVersionsPage /> },
                  { path: 'restaurants', element: <AdminRestaurantsPage /> },
                  {
                    path: 'restaurants/:restaurantId',
                    element: <AdminRestaurantDetailPage />,
                  },
                  { path: 'users', element: <AdminUsersPage /> },
                  { path: 'environment', element: <AdminEnvPage /> },
                  {
                    path: 'env',
                    element: <Navigate to="/admin/environment" replace />,
                  },
                ],
              },
            ],
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);
